import React from 'react';
import type { Inspection } from '../../types';
import { formatDate, isWithinDays } from '../../utils/scheduling';

interface Props {
  inspections: Inspection[];
  onClose: () => void;
}

export default function ReportOverlay({ inspections, onClose }: Props) {
  const recent = inspections
    .filter((i) => isWithinDays(i.DateInspected, 30))
    .sort((a, b) => b.DateInspected.localeCompare(a.DateInspected));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-10 pb-6 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col slide-in-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-[#4C9E00] flex items-center gap-2">
              🔔 Recent Inspections Report
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Last 30 days · {recent.length} inspection{recent.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {recent.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              No inspections in the last 30 days.
            </div>
          ) : (
            recent.map((insp, i) => (
              <div
                key={i}
                className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2"
              >
                {/* Header row */}
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{insp.Area}</span>
                      <span className="bg-[#EBF7E0] text-[#4C9E00] text-xs px-2 py-0.5 rounded-full font-medium">
                        {insp.AreaType}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatDate(insp.DateInspected)} · {insp.InspectedBy}
                    </p>
                  </div>
                  <span
                    className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                      insp.NumberOfConcerns > 0
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-50 text-[#4C9E00]'
                    }`}
                  >
                    {insp.NumberOfConcerns} concern{insp.NumberOfConcerns !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Detail grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                  <div>
                    <span className="text-gray-400">Supervisor</span>
                    <p className="font-medium text-gray-700">{insp.Supervisor}</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Red Taped</span>
                    <p className="font-medium text-gray-700">{insp.RedTaped}</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Highback</span>
                    <p className="font-medium text-gray-700">{insp.HighbackInspected}</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Period</span>
                    <p className="font-medium text-gray-700">{insp.Period} {insp.Year}</p>
                  </div>
                </div>

                {insp.Comment && (
                  <p className="text-xs text-gray-500 italic border-t border-gray-100 pt-2">
                    "{insp.Comment}"
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
